import { useState } from 'react'
import { AlertTriangle, CalendarDays, Check, Power, Users } from 'lucide-react'
import { clsx } from 'clsx'
import {
  useImportTeams,
  useImportCalendar,
  useUpdateCompetition,
  type AdminCompetition,
} from '../queries'
import styles from './CompetitionSetupCard.module.css'

type ImportSummary = { title: string; lines: string[]; warnings: string[] }

export function CompetitionSetupCard({ competition }: { competition: AdminCompetition }) {
  const [summary, setSummary] = useState<ImportSummary | null>(null)

  const importTeams    = useImportTeams()
  const importCalendar = useImportCalendar()
  const updateComp     = useUpdateCompetition()

  const anyPending = importTeams.isPending || importCalendar.isPending || updateComp.isPending
  const canImport  = !!competition.externalLeagueId && competition.season !== null

  function handleImportTeams() {
    importTeams.mutate(competition.id, {
      onSuccess: (r) => setSummary({
        title: 'Times importados',
        lines: [
          `${r.teamsFromApi} time(s) retornado(s) pela API`,
          `${r.teamsCreated} criado(s) · ${r.teamsUpdated} atualizado(s)`,
          ...(r.groupsAssigned > 0 ? [`${r.groupsAssigned} grupo(s) atribuído(s)`] : []),
        ],
        warnings: r.warnings,
      }),
    })
  }

  function handleImportCalendar() {
    importCalendar.mutate(competition.id, {
      onSuccess: (r) => setSummary({
        title: 'Calendário importado',
        lines: [
          `${r.fixturesFromApi} partida(s) retornada(s) pela API`,
          `${r.gameweeksCreated} rodada(s) criada(s)`,
          `${r.matchesCreated} partida(s) criada(s) · ${r.matchesUpdated} atualizada(s)`,
        ],
        warnings: r.warnings,
      }),
    })
  }

  function handleToggleActive() {
    updateComp.mutate({
      competitionId: competition.id,
      input:         { isActive: !competition.isActive },
    })
  }

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <div className={styles.headerMain}>
          <h3 className={styles.title}>{competition.name}</h3>
          <p className={styles.meta}>
            <span className={clsx(styles.badge, styles.badgeFormat)}>
              {competition.format === 'LEAGUE' ? 'LIGA' : 'MATA-MATA'}
            </span>
            <span className={clsx(
              styles.badge,
              competition.isActive ? styles.badgeActive : styles.badgeInactive,
            )}>
              {competition.isActive ? 'ATIVA' : 'INATIVA'}
            </span>
            <span className={styles.metaSep}>·</span>
            <span className={styles.mono}>{competition.slug}</span>
            {competition.externalLeagueId && (
              <>
                <span className={styles.metaSep}>·</span>
                <span className={styles.mono}>
                  API {competition.externalLeagueId}/{competition.season}
                </span>
              </>
            )}
          </p>
        </div>

        <button
          type="button"
          onClick={handleToggleActive}
          disabled={anyPending}
          className={clsx(styles.toggleBtn, competition.isActive && styles.toggleBtnOn)}
          title={competition.isActive
            ? 'Desativar — deixa de aparecer para novos jogos'
            : 'Ativar — passa a aparecer na criação de jogos'}
        >
          <Power size={13} />
          {competition.isActive ? 'Desativar' : 'Ativar'}
        </button>
      </header>

      {!canImport && (
        <p className={styles.warningBanner}>
          <AlertTriangle size={13} />
          Esta competição não tem <strong>código</strong> e <strong>temporada</strong>{' '}
          configurados — as importações não funcionam sem eles.
        </p>
      )}

      <div className={styles.steps}>
        <StepButton
          step={1}
          icon={<Users size={15} />}
          label="Importar times"
          hint="Cria os times da competição a partir do provedor de dados"
          disabled={!canImport || anyPending}
          isPending={importTeams.isPending}
          onClick={handleImportTeams}
        />
        <StepButton
          step={2}
          icon={<CalendarDays size={15} />}
          label="Importar calendário"
          hint="Cria as rodadas e partidas — rode depois dos times"
          disabled={!canImport || anyPending}
          isPending={importCalendar.isPending}
          onClick={handleImportCalendar}
        />
      </div>

      {summary && (
        <div className={styles.summary}>
          <div className={styles.summaryHeader}>
            <Check size={13} />
            {summary.title}
          </div>
          <ul className={styles.summaryList}>
            {summary.lines.map((line) => <li key={line}>{line}</li>)}
          </ul>

          {summary.warnings.length > 0 && (
            <div className={styles.warnings}>
              <div className={styles.warningsHeader}>
                <AlertTriangle size={12} />
                {summary.warnings.length} aviso{summary.warnings.length === 1 ? '' : 's'}
              </div>
              <ul className={styles.warningsList}>
                {summary.warnings.slice(0, 20).map((w, i) => <li key={i}>{w}</li>)}
              </ul>
              {summary.warnings.length > 20 && (
                <p className={styles.warningsMore}>
                  …e mais {summary.warnings.length - 20}.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

type StepButtonProps = {
  step:      number
  icon:      React.ReactNode
  label:     string
  hint:      string
  disabled:  boolean
  isPending: boolean
  onClick:   () => void
}

function StepButton({ step, icon, label, hint, disabled, isPending, onClick }: StepButtonProps) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={styles.stepBtn}>
      <span className={styles.stepIcon}>
        {isPending ? <span className={styles.spinner} aria-hidden="true" /> : icon}
      </span>
      <span className={styles.stepText}>
        <span className={styles.stepLabel}>
          <span className={styles.stepNumber}>{step}</span>
          {label}
        </span>
        <span className={styles.stepHint}>{hint}</span>
      </span>
    </button>
  )
}
